import { WidgetContext, type WidgetData } from './context';
import { WidgetHost } from './widgets';
import type { ZoneTemplate } from './types';
import type { FrameStyle, Theme } from '../channels/scoreboard/themes';

// Border treatment applied to zone panels that carry a bg tint.
function framePanel(frame: FrameStyle, theme: Theme): React.CSSProperties {
  switch (frame) {
    case 'rounded':
      return { borderRadius: 14, border: `1px solid ${theme.br}` };
    case 'angular':
      return { borderRadius: 0, border: `2px solid ${theme.ac}` };
    case 'borderless':
      return {};
    case 'glass':
      return { borderRadius: 12, border: `1px solid ${theme.br}`, backdropFilter: 'blur(8px)', background: 'rgba(255,255,255,0.06)' };
    case 'flat':
    default:
      return { borderRadius: 3, border: `1px solid ${theme.br}` };
  }
}

// Lays out a ZoneTemplate: each zone is absolutely positioned in percent
// coordinates and mounts the widget its binding names. Widgets read live
// data + theme from WidgetContext, so the same template renders sample or
// live, in any theme.
export function ZoneRenderer({
  template,
  data,
}: {
  template: ZoneTemplate;
  data: WidgetData;
}) {
  const bg = template.background;
  // Base canvas fill: theme color, solid, or gradient.
  let background = data.theme.bg;
  if (bg?.type === 'solid' && bg.color) background = bg.color;
  else if (bg?.type === 'gradient' && bg.from && bg.to) {
    background = `linear-gradient(135deg, ${bg.from}, ${bg.to})`;
  }
  const hasImage = bg?.type === 'image' && !!bg.imageUrl;

  return (
    <WidgetContext.Provider value={data}>
      <div
        style={{
          position: 'relative',
          width: '100%',
          height: '100%',
          background,
          overflow: 'hidden',
          fontFamily: data.font,
        }}
      >
        {hasImage && (
          <>
            <div
              style={{
                position: 'absolute',
                inset: 0,
                backgroundImage: `url(${bg!.imageUrl})`,
                backgroundSize: bg!.fit ?? 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
              }}
            />
            {(bg!.overlay ?? 0) > 0 && (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  background: `rgba(0,0,0,${bg!.overlay})`,
                }}
              />
            )}
          </>
        )}
        {template.zones.map((z) => {
          const binding = template.bindings[z.id];
          return (
            <div
              key={z.id}
              style={{
                position: 'absolute',
                left: `${z.x}%`,
                top: `${z.y}%`,
                width: `${z.w}%`,
                height: `${z.h}%`,
                background: z.bg,
                padding: 6,
                boxSizing: 'border-box',
                ...(z.bg ? framePanel(data.frame, data.theme) : {}),
              }}
            >
              {binding ? <WidgetHost binding={binding} /> : null}
            </div>
          );
        })}
      </div>
    </WidgetContext.Provider>
  );
}
